import {EntitlementProvider} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {CopilotLicenseType} from '@github-ui/copilot-chat-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CommandIconButton} from '@github-ui/ui-commands'
import {PaperAirplaneIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {useEffect, useMemo, useRef, useState} from 'react'

import styles from './ChatInput.module.css'
import {ModelPicker} from './ModelPicker'
import ModelPickerV2 from './ModelPickerV2/ModelPickerV2'
import {getModelPickerData} from './ModelPickerV2/ModelPickerV2.data'

export interface ChatInputProps {
  currentModelId?: string | null
  prompt: string
  onPromptChange: (prompt: string) => void
  onOpenDialog: () => void
  onModelClick: (modelId: string) => void
}

export const ChatInput = ({currentModelId, prompt, onPromptChange, onOpenDialog, onModelClick}: ChatInputProps) => {
  const [isModelPickerOpen, setIsModelPickerOpen] = useState<boolean>(false)
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const isHyperspace2025LoggedOutBatch2 = isFeatureEnabled('hyperspace_2025_logged_out_batch_2')
  const maxUpsellEnabled = isFeatureEnabled('copilot_chat_max_upsell')
  const itemGroups = useMemo(() => getModelPickerData({maxUpsellEnabled}), [maxUpsellEnabled])

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onPromptChange(event.target.value)
  }

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [prompt])

  useEffect(() => {
    if (!textareaRef.current) return
    textareaRef.current.focus()
  }, [])

  return (
    <form className={clsx(styles.container, isInputFocused && styles.containerFocused)}>
      <div className={styles.inputContainer}>
        <textarea
          ref={textareaRef}
          className={styles.input}
          placeholder="Ask Copilot"
          disabled={false}
          value={prompt}
          rows={1}
          onFocus={event => {
            setIsInputFocused(true)
            event.target.setSelectionRange(0, event.target.value.length)
          }}
          onBlur={() => setIsInputFocused(false)}
          onChange={event => {
            handleInputChange(event)
            if (textareaRef.current) {
              textareaRef.current.style.height = 'auto'
              textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
            }
          }}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              if (prompt.trim() !== '') {
                onOpenDialog()
              }
            }
          }}
        />
      </div>

      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft} />

        <div className={styles.toolbarRight}>
          <div>
            <EntitlementProvider initialLicenseType={CopilotLicenseType.Unlicensed}>
              {!isHyperspace2025LoggedOutBatch2 && <ModelPicker initialModelID={currentModelId} />}
              {isHyperspace2025LoggedOutBatch2 && (
                <ModelPickerV2
                  isOpen={isModelPickerOpen}
                  onOpenChange={setIsModelPickerOpen}
                  currentModelId={currentModelId}
                  // TODO: Feed data from CAPI once available
                  itemGroups={itemGroups}
                  onModelClick={onModelClick}
                  onSignInClick={() => {
                    onOpenDialog()
                    setIsModelPickerOpen(false)
                  }}
                  // TODO: Update once CAPI allows model selection for logged out users
                  canSelectModels={false}
                  isFreeModelsOnly
                />
              )}
            </EntitlementProvider>
          </div>
          <div className={styles.trailingActions}>
            <CommandIconButton
              commandId="copilot-chat:send-message"
              variant="invisible"
              size="medium"
              icon={PaperAirplaneIcon}
              aria-label="Send now"
              tooltipDirection="n"
              onClick={() => {
                if (prompt.trim() !== '') {
                  onOpenDialog()
                }
              }}
            />
          </div>
        </div>
      </div>
    </form>
  )
}
